/**
 * ContactConfig.js
 * -------------------------------
 * SSR-compatible utility module for extracting and applying contact modal configuration.
 *
 * Responsibilities:
 * - Safely reads configuration from contact button data attributes (`data-*`)
 * - Dynamically applies form field customizations to the modal (e.g., message placeholder, prefilled text, checkbox options)
 * - Provides safe DOM access methods that won't break in SSR environments
 * - Ensures security through XSS-safe text and ID sanitization
 * - Includes default config handling and merging logic
 * - Essential error tracking for critical configuration failures
 *
 * Features:
 * - Works in both browser and server-side environments (SSR-safe)
 * - All DOM operations are gated with runtime `isBrowserEnvironment()` checks
 * - Logs warnings for failed operations instead of throwing runtime errors
 * - Avoids hard dependency on specific DOM presence during import
 * - Minimal error tracking for essential configuration failures
 * - Basic debugging capabilities for troubleshooting production issues
 *
 * Limitations:
 * - Requires certain DOM elements (e.g., `#contact-modal`, `.checkbox-group`) to apply configurations effectively
 * - Assumes input field IDs and modal structure follow naming conventions (e.g., `#message`)
 * - Checkbox replacement clears all existing options and replaces them entirely
 * - Does not debounce or validate rapid repeated configuration updates
 * - Error tracking is minimal to avoid code explosion (only critical failures)
 * - Error data is stored in memory (non-persistent across page reloads)
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && typeof document.getElementById === 'function';

export default class ContactConfig {
  static _errorHandler = null;

  /**
   * Initialize static error handler
   */
  static _initErrorHandler() {
    if (this._errorHandler) return;
    try {
      this._errorHandler = new ErrorHandler({ module: 'ContactConfig', maxErrors: 15 });
    } catch {
      this._errorHandler = {
        handleError: () => {},
        handleFatalError: () => {},
        getRecentErrors: () => [],
        hasFatalErrors: () => false,
        getErrorStats: () => ({ totalErrors: 0, fatalErrors: 0 })
      };
    }
  }

  /**
   * Check if in browser environment
   */
  static isBrowserEnvironment() {
    return isBrowser;
  }

  /**
   * Safely get element by ID
   */
  static safeGetElement(id) {
    if (!this.isBrowserEnvironment()) return null;
    try {
      return document.getElementById(id);
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Failed to get DOM element', err, { elementId: id });
      return null;
    }
  }

  /**
   * Safely query selector
   */
  static safeQuerySelector(selector, context = document) {
    if (!this.isBrowserEnvironment()) return null;
    try {
      return context.querySelector(selector);
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Failed to query selector', err, { selector });
      return null;
    }
  }

  /**
   * Safely query selector all
   */
  static safeQuerySelectorAll(selector, context = document) {
    if (!this.isBrowserEnvironment()) return [];
    try {
      return context.querySelectorAll(selector);
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Failed to query selector all', err, { selector });
      return [];
    }
  }

  /**
   * Safely create element
   */
  static safeCreateElement(tag) {
    if (!this.isBrowserEnvironment()) return null;
    try {
      return document.createElement(tag);
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Failed to create element', err, { tag });
      return null;
    }
  }

  /**
   * Extract config from button dataset
   */
  static getButtonConfig(button) {
    if (!this.isBrowserEnvironment() || !button || !button.dataset) return null;
    const config = {};
    let has = false;
    try {
      if (button.dataset.contactType) {
        config.type = button.dataset.contactType;
        has = true;
      }
      if (button.dataset.contactMessage) {
        config.messagePlaceholder = button.dataset.contactMessage;
        has = true;
      }
      if (button.dataset.contactServices) {
        const services = button.dataset.contactServices.split(',').map(s => s.trim());
        if (services.length) {
          config.checkboxOptions = services;
          has = true;
        }
      }
      if (button.dataset.contactPrefill) {
        config.prefillMessage = button.dataset.contactPrefill;
        has = true;
      }
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Error parsing button config', err, { buttonId: button.id });
    }
    return has ? config : null;
  }

  /**
   * Apply configuration to modal
   */
  static applyModalConfig(config, modalEl = null) {
    if (!this.isBrowserEnvironment() || !config) return false;
    const modal = modalEl || this.safeGetElement('contact-modal');
    if (!modal) return false;
    let applied = false;
    try {
      if (config.messagePlaceholder) {
        const field = this.safeQuerySelector('#message', modal);
        if (field) {
          field.placeholder = config.messagePlaceholder;
          applied = true;
        }
      }
      if (config.prefillMessage) {
        const field = this.safeQuerySelector('#message', modal);
        if (field) {
          field.value = config.prefillMessage;
          applied = true;
        }
      }
      if (config.checkboxOptions) {
        applied = this.updateServiceOptions(config.checkboxOptions, modal) || applied;
      }
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleFatalError('Failed applying modal config', err);
    }
    return applied;
  }

  /**
   * Update checkbox options
   */
  static updateServiceOptions(options, modalEl = null) {
    if (!this.isBrowserEnvironment() || !Array.isArray(options)) return false;
    const modal = modalEl || this.safeGetElement('contact-modal');
    if (!modal) return false;
    const group = this.safeQuerySelector('.checkbox-group', modal);
    if (!group) return false;
    // remove all and add new
    group.innerHTML = '';
    options.forEach((opt, i) => {
      const label = this.safeCreateElement('label');
      if (!label) return;
      label.className = 'checkbox-label';
      const sanitized = this.sanitizeText(opt);
      const id = this.sanitizeId(`service-${i}`);
      label.innerHTML = `<input type="checkbox" name="services" id="${id}" value="${sanitized}"><span>${sanitized}</span>`;
      group.appendChild(label);
    });
    return true;
  }

  /**
   * Sanitize text
   */
  static sanitizeText(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .trim();
  }

  /**
   * Sanitize ID
   */
  static sanitizeId(id) {
    return String(id)
      .replace(/[^a-zA-Z0-9_-]/g, '-')
      .replace(/^[^a-zA-Z]/, 'id-')
      .toLowerCase();
  }

  /**
   * Default configuration
   */
  static getDefaultConfig() {
    return { type: 'general', messagePlaceholder: 'How can we help?', checkboxOptions: [], prefillMessage: '' };
  }

  /**
   * Merge with defaults
   */
  static mergeWithDefaults(cfg) {
    const defaults = this.getDefaultConfig();
    if (!cfg || typeof cfg !== 'object') return defaults;
    return { ...defaults, ...cfg };
  }

  /**
   * Environment checks
   */
  static isEnvironmentSupported() {
    return this.isBrowserEnvironment();
  }

  /**
   * Error stats
   */
  static getRecentErrors(min = 5) {
    this._initErrorHandler();
    return this._errorHandler.getRecentErrors(min);
  }

  static hasCriticalIssues() {
    this._initErrorHandler();
    return this._errorHandler.hasFatalErrors();
  }

  static getErrorStats() {
    this._initErrorHandler();
    return this._errorHandler.getErrorStats();
  }
}
